import React, { useRef, useState, useEffect, MouseEvent } from 'react';

import cn from 'classnames';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ClickAwayListener, Grow, MenuList, Paper, Popper, MenuItem, Box } from '@mui/material';

import OTooltip from '@src/shared/OTooltip';
import CircularProgress from '@mui/material/CircularProgress';

import styles from './ODropdownSplitButton.module.scss';

export interface IDropdownButtonProps {
  text: string | React.ReactNode;
  variant?: 'primary' | 'alternative' | 'success' | 'danger' | 'cancel' | 'approve';
  loading?: boolean;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  disableMainButton?: boolean;
  onClick?: () => void;
  dropdownModel: {
    label: string;
    command?: () => void;
    disabled?: boolean;
    tooltip?: string;
  }[];
  isSplitTransparent?: boolean;
  tooltip?: string;
}

const ODropdownSplitButton = ({
  text,
  onClick,
  dropdownModel,
  loading = false,
  disabled = false,
  variant = 'primary',
  isSplitTransparent = false,
  tooltip = '',
  disableMainButton = false,
  size = 'medium',
}: IDropdownButtonProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const idRef = useRef(Math.random().toString(36).substr(2));

  useEffect(() => {
    const onToggle = (e: CustomEvent) => {
      if (e.detail !== idRef.current) setOpen(false);
    };
    window.addEventListener('DropdownSplitButton:toggle', onToggle as EventListener);
    return () => window.removeEventListener('DropdownSplitButton:toggle', onToggle as EventListener);
  }, []);

  const broadcastOpen = () =>
    window.dispatchEvent(new CustomEvent('DropdownSplitButton:toggle', { detail: idRef.current }));

  const handleToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (loading || disabled) return;
    const next = !open;
    if (next) broadcastOpen();
    setOpen(next);
  };

  const handleMainClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
      setOpen(false);
    } else {
      handleToggle(e);
    }
  };

  return (
    <OTooltip
      title={tooltip}
      arrow
      placement="top"
      backgroundColor="#3E4756"
      customStyles={{
        tooltip: {
          backgroundColor: '#3E4756',
          color: 'white',
          fontSize: '12px',
          lineHeight: '1.2',
          maxWidth: 250,
          padding: '8px 12px',
          whiteSpace: 'pre-wrap',
        },
        arrow: {
          color: '#3E4756',
        },
      }}
    >
      <Box
        ref={containerRef}
        className={cn(styles.buttonContainer, styles.boxContainer, styles[size], styles[variant], {
          [styles.disabled]: disabled,
          [styles.splitButtonTransparent]: isSplitTransparent,
        })}
      >
        <button
          className={cn(styles.splitButtonMain, styles[variant], {
            [styles.disabledButton]: disabled || disableMainButton,
          })}
          onClick={handleMainClick}
          disabled={disabled || loading || disableMainButton}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : text}
        </button>
        <button
          className={cn(styles.splitButtonDropdown, styles[variant], {
            [styles.disabledButton]: disabled,
            [styles.splitButtonTransparent]: isSplitTransparent,
          })}
          onClick={handleToggle}
          disabled={disabled || loading}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </button>
        <ClickAwayListener onClickAway={() => setTimeout(() => setOpen(false), 100)}>
          <Popper
            open={open}
            anchorEl={containerRef.current}
            placement="bottom"
            transition
            disablePortal={false}
            modifiers={[
              { name: 'flip', enabled: true, options: { altAxis: true, altBoundary: true } },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  altAxis: true,
                },
              },
            ]}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
                <Paper elevation={3} className={styles.dropdownMenuPaper}>
                  <MenuList autoFocusItem={open} className={styles.dropdownMenuList}>
                    {dropdownModel.map((item, idx) => (
                      <OTooltip
                        key={idx}
                        title={item.tooltip || ''}
                        arrow
                        placement="left"
                        customStyles={{
                          tooltip: {
                            backgroundColor: '#3E4756',
                            color: 'white',
                            fontSize: '12px',
                            maxWidth: 250,
                            padding: '8px 12px',
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.2',
                          },
                          arrow: {
                            color: '#3E4756',
                          },
                        }}
                      >
                        <Box>
                          <MenuItem
                            disabled={item.disabled}
                            onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                              e.stopPropagation();
                              if (!item.disabled) {
                                item.command?.();
                                setOpen(false);
                              }
                            }}
                            className={styles.dropdownMenuItem}
                          >
                            {item.label}
                          </MenuItem>
                        </Box>
                      </OTooltip>
                    ))}
                  </MenuList>
                </Paper>
              </Grow>
            )}
          </Popper>
        </ClickAwayListener>
      </Box>
    </OTooltip>
  );
};

export default ODropdownSplitButton;
